import { LockOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Tooltip } from 'components/ui';
import { useAccountContext } from 'utils/account/AccountContext';
import { Theme, ThemeStorageKey } from 'utils/color/horizonTheme';
import { LOGO_FULL, LOGO_ICON, SKYSPECS_LOGO, SKYSPECS_LOGO_WHITE } from 'utils/constants';
import { APP_VERSION } from 'utils/env';
import { SIDEBAR_IMPROVEMENTS } from 'utils/release-toggles';
import { getItem as getLocalStorageItem, setItem as setLocalStorageItem } from 'utils/storage';

import {
  Image,
  Logo,
  PrivacyPolicyLink,
  SidebarFooter,
  SidebarLogoImage,
  SidebarLogoLink,
  SidebarMeta,
  StyledMenu,
  StyledSider,
  VersionLabel,
} from './SideNav.style';
import { type Tab } from './types';

const sidebarCollapsedStorageKey = 'sidebar-collapsed';

interface Props {
  tabs: Tab[];
}

export const SideNav = ({ tabs }: Props) => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState<boolean | undefined>(undefined);
  const { pathname } = useLocation();
  const { hasReleaseToggle } = useAccountContext();
  const hasSidebarImprovementsRT = hasReleaseToggle(SIDEBAR_IMPROVEMENTS);
  const sideNavLogoSrc = collapsed
    ? '/favicon.png'
    : theme.themeName === Theme.Dark
      ? SKYSPECS_LOGO_WHITE
      : SKYSPECS_LOGO;

  const activeTab = tabs
    .filter(({ link, subRoutes = [] }) => {
      const routes = [link, ...subRoutes];
      return !isEmpty(routes.filter(route => !!route && pathname.startsWith(route)));
    })
    .map(({ id }) => id);

  const handleCollapse = (collapsed: boolean, type: 'clickTrigger' | 'responsive') => {
    // Save in local storage if this was a user action (i.e. not responsive behavior)
    if (hasSidebarImprovementsRT && type === 'clickTrigger') {
      setLocalStorageItem(sidebarCollapsedStorageKey, String(collapsed));
    }

    // Skip reopening when window is expanded and user has collapsed the sidebar previously
    if (
      type === 'responsive' &&
      collapsed === false &&
      getLocalStorageItem(sidebarCollapsedStorageKey) === 'true'
    ) {
      return;
    }
    setCollapsed(prevState => !prevState);
  };

  useEffect(() => {
    if (hasSidebarImprovementsRT) {
      const collapsedPreference = getLocalStorageItem(sidebarCollapsedStorageKey) === 'true';
      setCollapsed(collapsedPreference);
    }
  }, [hasSidebarImprovementsRT]);

  // Remove when cleaning up 'rtv1:sidebar_improvements_q3_2025'
  useEffect(() => {
    if (!hasSidebarImprovementsRT) {
      // let antd handle collapse for < lg breakpoint
      if (window.innerWidth < 992) {
        return;
      }

      // collapse sidebar when analytics is active
      setCollapsed(pathname.startsWith('/analytics'));
    }
  }, [pathname, setCollapsed, hasSidebarImprovementsRT]);

  return (
    <StyledSider
      breakpoint="lg"
      collapsed={collapsed}
      data-testid="sidebar-nav"
      collapsible={hasSidebarImprovementsRT}
      onCollapse={handleCollapse}
      $styledComponentsTheme={theme}
    >
      <Logo>
        <Link to="/">
          <Image
            $collapsed={collapsed}
            alt="Horizon Logo"
            src={collapsed ? LOGO_ICON : LOGO_FULL}
          />
        </Link>
      </Logo>
      <StyledMenu
        theme={getLocalStorageItem(ThemeStorageKey)}
        selectedKeys={activeTab}
        style={{
          padding: collapsed ? '1em 0' : '1em',
          borderRight: 'none',
        }}
        items={tabs.map(
          ({ id, title, icon, link, target, disabled, disabledTooltip, noLinkTooltip }) => {
            // Allow components to soft-reload themselves if navigating to the current path
            const isSelfLink = link === pathname;
            return {
              label: disabled ? (
                <Tooltip title={disabledTooltip} placement="right">
                  {title}
                </Tooltip>
              ) : link ? (
                <Link
                  replace={isSelfLink}
                  to={{ pathname: link, state: { softReload: isSelfLink } }}
                  target={target}
                >
                  {title}
                </Link>
              ) : noLinkTooltip ? (
                <Tooltip title={noLinkTooltip} placement="right">
                  {title}
                </Tooltip>
              ) : (
                title
              ),
              icon,
              disabled,
              key: id,
              'data-testid': id,
            };
          }
        )}
      />
      <SidebarMeta>
        <VersionLabel $collapsed={collapsed} data-testid="app-version">
          {collapsed ? APP_VERSION : `Version ${APP_VERSION}`}
        </VersionLabel>
        <SidebarLogoLink $collapsed={collapsed} to="/">
          <SidebarLogoImage
            $collapsed={collapsed}
            $invertForDark={collapsed && theme.themeName === Theme.Dark}
            alt="SkySpecs Logo"
            src={sideNavLogoSrc}
          />
        </SidebarLogoLink>
        <SidebarFooter $collapsed={collapsed}>
          <Tooltip title={collapsed ? 'Privacy Policy' : undefined} placement="right">
            <PrivacyPolicyLink
              aria-label={collapsed ? 'Privacy Policy' : undefined}
              target="_blank"
              href="https://skyspecs.com/privacy/"
              rel="noopener noreferrer"
            >
              <LockOutlined />
              {!collapsed ? 'Privacy Policy' : null}
            </PrivacyPolicyLink>
          </Tooltip>
        </SidebarFooter>
      </SidebarMeta>
    </StyledSider>
  );
};
